/**
 * Swedish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal du (locked in l10n/locales.md).
 *
 * Platform terms follow Swedish UI conventions (Tema for theme,
 * konversation for conversation, parkoppla for pair, avvisa for
 * dismiss, mottagare for a receiving surface); "Glance" is the
 * untranslated feature brand name (glossary D1) and "klipp" is the
 * locked loanword for a single clipboard item (D2 — the native word
 * IS the loan's natural form).
 *
 * Plural notes: Swedish is a {one, other} CLDR locale, and its count
 * nouns inflect (träff → träffar, enhet → enheter) while some are
 * invariant (samtal, tecken), so every count-bearing function selects
 * through `selectPlural("sv", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Ansluten",
    statusReconnecting: "Återansluter…",
    statusDisconnected: "Frånkopplad",
    openSettings: "Öppna inställningar",
    pairedFallbackDevice: "din telefon",
    publicModeToggleEnable: "Dölj aviseringsinnehåll",
    publicModeToggleDisable: "Visa aviseringsinnehåll",
  },

  notifications: {
    replyAriaLabel: "Svara",
    replyPlaceholder: "Svara…",
    replySend: "Skicka",
    sent: "Skickat",
    failedToSend: "Kunde inte skicka",
    unread: "Oläst",
    dismiss: "Avvisa",
    notificationImageAlt: "Aviseringsbild",
  },

  conversations: {
    replyAriaLabel: "Svara",
    replyPlaceholder: "Svara…",
    replySend: "Skicka",
    sent: "Skickat",
    failedToSend: "Kunde inte skicka",
    dismiss: "Avvisa konversation",
    messagesListLabel: "Meddelanden i den här konversationen",
    deviceOwner: "Du",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Inga olästa meddelanden"
        : `${count} ${selectPlural("sv", count, {
            one: "oläst meddelande",
            other: "olästa meddelanden",
          })}`,
  },

  list: {
    today: "Idag",
    yesterday: "Igår",
    searchNotifications: "Sök aviseringar",
    clearSearch: "Rensa sökning",
    clearAll: "Rensa alla",
    markAllRead: "Markera alla som lästa",
    groupCheckboxLabel: "Gruppera",
    noNotificationsTitle: "Inga aviseringar ännu",
    noNotificationsDesc: "Nya meddelanden som speglas från din telefon visas här.",
    noMatchesTitle: "Inga träffar",
    notificationsList: "Aviseringar",
    privacyAppList: "Aviseringar per app",
    clear: "Rensa",
    clearMatches: (count: number): string =>
      `Rensa ${count} ${selectPlural("sv", count, { one: "träff", other: "träffar" })}`,
    noMatchesDesc: (query: string): string => `Inga aviseringar matchar ”${query}”.`,
    appNotificationsGroup: (appName: string): string => `Aviseringar från ${appName}`,
    dismissAllFrom: (appName: string): string => `Avvisa alla från ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("sv", count, { one: "meddelande", other: "meddelanden" })
          : kind === "calls"
            ? selectPlural("sv", count, { one: "samtal", other: "samtal" })
            : selectPlural("sv", count, { one: "avisering", other: "aviseringar" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "avvisa",
    kbdNavigate: "navigera",
    kbdOrSeparator: "eller",
  },

  settings: {
    back: "Tillbaka till aviseringar",
    settings: "Inställningar",
    general: "Allmänt",
    theme: "Tema",
    themeAuto: "Automatiskt",
    themeLight: "Ljust",
    themeDark: "Mörkt",
    language: "Språk",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Aviseringar",
    osNotifications: "Systemaviseringar",
    callNotifications: "Samtalsaviseringar",
    privacySecurity: "Integritet",
    analyticsToggle: "Anonym användningsstatistik",
    analyticsToggleDescription: "Endast antal och milstolpar — aldrig dina aviseringar.",
    statusConnected: "Ansluten",
    statusReconnecting: "Återansluter…",
    statusDisconnected: "Frånkopplad",
    disconnectFromPhone: "Koppla från telefonen",
    disconnectConfirm: "Är du säker? Det här tar bort parkopplingen för din enhet.",
    yesDisconnect: "Ja, koppla från",
    cancel: "Avbryt",
    version: "Version",
    encryption: "Kryptering",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Behöver du hjälp? Mejla __EMAIL__",
    disconnectFailed: "Kunde inte koppla från – försök igen",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Frånkopplad, men den lokala rensningen misslyckades: ${errorMsg}. Ladda om sidan.`,
    disconnectLocalCleanupFailed:
      "Frånkopplad, men den lokala rensningen misslyckades. Ladda om sidan.",
  },

  pairing: {
    enterCodeInstruction: "Ange den här koden i Reflecto-appen",
    pairedSuccessfully: "Parkopplingen lyckades!",
    pairingFailed: "Parkopplingen misslyckades",
    requestingPairingCode: "Begär parkopplingskod…",
    codeExpired: "Koden har gått ut",
    tryAgain: "Försök igen",
    failedToStartPairing: "Kunde inte starta parkopplingen",
    installHeading: "Har du inte appen än?",
    installScanHint: "Skanna med telefonens kamera",
    installQrLabel: "QR-kod för att installera Reflecto på telefonen",
    capabilitiesHeading: "Efter parkopplingen kan du:",
    capabilityMirror: "Se telefonens aviseringar här",
    capabilityReply: "Svara utan att ta upp telefonen",
    capabilityShare: "Dela urklipp och filer åt båda hållen",
    encryptionNote: "End-to-end-krypterat. Servern ser aldrig ditt innehåll.",
  },

  dnd: {
    title: "Stör ej",
    description: "Pausa aviseringar på den här enheten den närmaste timmen.",
    snooze1h: "Pausa 1 timme",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv till ${formattedTime}`,
    cancelSnooze: "Avbryt",
  },

  connectors: {
    title: "Anslutna tjänster",
    emptyBody:
      "Inga anslutna tjänster ännu. Anslut ChatGPT eller Claude via deras anslutningsinställningar och godkänn sedan koden på din telefon.",
    scopeSend: "Kan skicka aviseringar",
    added: (date: string): string => `Tillagd ${date}`,
    lastUsed: (relative: string): string => `Senast använd ${relative}`,
    neverUsed: "Aldrig använd",
    revoke: "Koppla från",
    revokeConfirm: (label: string): string => `Koppla från ${label}? Åtkomsten upphör omedelbart.`,
    revokeAction: "Koppla från",
    revokeConfirmLabel: (label: string): string => `Koppla från ${label}`,
    cancel: "Avbryt",
    loadError: "Kunde inte läsa in anslutna tjänster. Försök igen.",
    revokeError: "Kunde inte koppla från tjänsten. Försök igen.",
    retry: "Försök igen",
  },

  safetyNumber: {
    rowLabel: "Verifiera kryptering",
    rowSublabel: "Jämför säkerhetsnummer med din telefon",
    cardTitle: "Säkerhetsnummer",
    caption: (deviceLabel: string): string =>
      `Jämför med numret på ${deviceLabel}. Om båda matchar är din end-to-end-kryptering intakt.`,
    repairNote: "Numret ändras när du parkopplar en enhet.",
    copy: "Kopiera",
    copied: "Kopierad",
    maskedHint: "Dolt medan sekretessläget är på",
  },

  feedback: {
    title: "Skicka feedback",
    close: "Stäng",
    messageLabel: "Ditt meddelande",
    messagePlaceholder: "Hittade du en bugg, har du en idé eller känns något fel? Berätta för oss.",
    categoryLabel: "Kategori",
    categoryBug: "Bugg",
    categoryIdea: "Idé",
    categoryOther: "Övrigt",
    emailLabel: "E-post (valfritt, endast om du vill ha svar)",
    emailPlaceholder: "du@exempel.se",
    emailHelp: "Vi lägger inte till dig i någon lista.",
    disclosure:
      "Ditt meddelande skickas till Reflectos server och registreras i vår interna ärendehanterare. Vi bifogar din appversion och ett enhets-ID som tilldelas vid parkoppling så att vi kan koppla samman rapporter. Vi ser inte dina aviseringar, kontakter eller andra personuppgifter. E-post är valfritt – ange det endast om du vill ha svar. Vi lägger inte till dig i någon lista.",
    submit: "Skicka feedback",
    sending: "Skickar…",
    success: "Tack – läses av en människa, oftast inom 24 h",
    error: "Kunde inte skicka. Försök igen.",
    done: "Klar",
    sendFeedback: "Skicka feedback",
  },

  uninstall: {
    headline: "Reflectos tillägg är avinstallerat",
    body: "Det fanns aldrig något konto, och tillägget sparade din parkoppling på den här datorn, så det finns inget kvar för dig att rensa bort.",
    ask: "Om du har trettio sekunder vill vi gärna veta vad som gick fel.",
    categoryLegend: "Vilken sorts feedback är det här?",
    categoryBug: "Något gick sönder",
    categoryIdea: "Det saknades något jag behövde",
    categoryOther: "Något annat",
    messageLabel: "Vad gick fel?",
    messagePlaceholder: "Aviseringarna slutade komma fram efter några timmar.",
    messageRequired: "Skriv en mening först och skicka sedan.",
    charactersLeft: (count: number): string => `${count} tecken kvar`,
    emailLabel: "E-post (valfritt)",
    emailPlaceholder: "du@exempel.se",
    emailHelp: "Används bara för att svara dig. Inget annat.",
    submit: "Skicka feedback",
    sending: "Skickar…",
    retry: "Försök igen",
    privacyNote:
      "Det här formuläret är inte kopplat till någon enhet. Det innehåller inga av dina aviseringar, ditt urklipp eller dina filer. E-post är valfritt och används bara för att svara.",
    success: "Tack. Varje sådant meddelande läses av en människa.",
    errorGeneric: "Det skickades inte. Ditt meddelande finns kvar.",
    errorRateLimited:
      "Du har skickat flera meddelanden nyligen. Försök igen om ungefär en timme. Din text ligger kvar.",
    footerWebApp: "Reflectos webbapp fungerar fortfarande utan tillägget.",
    footerWebAppLink: "Öppna webbappen",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Stäng",
    aiSummaryTag: "AI-sammanfattning",
    errorAi: "Kunde inte skapa en sammanfattning just nu.",
    retryCta: "Försök igen",
    downloadHeading: "Få AI-sammanfattningar",
    downloadBody:
      "Ta igen det du missat med en AI-sammanfattning på enheten. Körs helt på din enhet.",
    downloadCta: "Ladda ner modellen",
    downloadPrivacyNote: "En engångsnedladdning. Servern ser aldrig dina aviseringar.",
    downloadingHeading: "Laddar ner modellen…",
    caughtUp: "Du är helt ikapp – inget kräver din uppmärksamhet just nu.",
    needsYou: "Kräver uppmärksamhet",
    quiet: "Övrigt",
    scanning: (pending: number): string => `Sammanfattar ${pending}…`,
    jump: "Öppna avisering",
    affordanceReply: "Svara",
    affordanceOpen: "Öppna",
    affordanceCopyOtp: "Kopiera kod",
    buttonLabel: "Glance",
  },

  update: {
    title: "Uppdatering tillgänglig",
    description: "En ny version av Reflecto är klar. Ladda om för att uppdatera.",
    reload: "Ladda om",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Vi döpte den här enheten till ”${name}”.`,
    cardLooksGood: "Ser bra ut",
    cardEdit: "Redigera",
    cardAriaLabel: "Bekräfta enhetsnamn",
    renameTitle: "Byt namn på den här enheten",
    renameLabel: "Enhetsnamn",
    renameSave: "Spara",
    renameCancel: "Avbryt",
    renameFailed: "Kunde inte byta namn. Försök igen.",
  },

  collision: {
    title: (name: string): string => `Du har redan en ”${name}”`,
    subtitle: "Vad ska vi kalla den här?",
    chipWork: (name: string): string => `Jobb ${name}`,
    chipHome: (name: string): string => `Hem ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Anpassat namn",
    save: "Spara",
    useAnyway: "Använd det ursprungliga namnet ändå",
    saveFailed: "Kunde inte spara det nya namnet. Försök igen.",
    ariaLabel: "Åtgärda dubblett av enhetsnamn",
  },

  clipboard: {
    title: "Urklipp",
    emptyTitle: "Inga klipp ännu",
    empty: "Klipp från dina andra enheter visas här. Tryck för att kopiera.",
    from: (label: string): string => `från ${label}`,
    to: (label: string): string => `till ${label}`,
    copy: "Kopiera till urklipp",
    copied: "Kopierat",
    reveal: "Visa",
    hide: "Dölj",
    maskedHint: "Innehåll dolt – tryck på Visa för att se det",
    sensitiveChip: "Känsligt",
    cloudChip: "från din API-token",
    delete: "Ta bort klipp",
    sendPlaceholder: "Klistra in text att skicka…",
    sendCta: "Skicka",
    sendSensitiveToggle: "Markera som känsligt",
    sendSuccess: (n: number): string =>
      `Skickat till ${n} ${selectPlural("sv", n, { one: "enhet", other: "enheter" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Nådde ${n}/${total} ${selectPlural("sv", total, { one: "enhet", other: "enheter" })}.`,
    sendNoDevices: "Inga parkopplade enheter att skicka till.",
    sendFailed: "Kunde inte skicka. Försök igen.",
    sendEmpty: "Inget att skicka.",
    dockRecentHeading: "Senaste",
    dockEmptyClipboard: "Ditt urklipp är tomt.",
    clearAll: "Rensa alla",
    clearAllConfirm: "Ja, rensa alla",
    clearAllCancel: "Avbryt",
    shareTitle: "Skicka till dina enheter",
    shareReview:
      "Granska vad du skickar. Det går end-to-end-krypterat till dina parkopplade enheter.",
    shareSend: "Skicka",
    shareCancel: "Avbryt",
    shareNotPaired: "Parkoppla en enhet först för att skicka delad text.",
    shareOpenApp: "Öppna Reflecto",
    pasteFromClipboard: "Klistra in från urklipp",
    readFailed: "Det gick inte att läsa urklippet — klistra in nedan.",
  },
  install: {
    title: "Installera Reflecto",
    body: "Lägg till på din startskärm för snabbare åtkomst.",
    cta: "Installera",
    dismissAria: "Avvisa installationsuppmaningen",
  },
  nav: {
    home: "Hem",
    clipboard: "Urklipp",
    files: "Filer",
    ariaLabel: "Huvudnavigering",
    fileHiddenPublicMode: "Dolt medan integritetsläget är på.",
  },
  common: {
    loading: "Läser in",
  },
  errors: {
    notPaired: "Inte parkopplad",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} på ${os}`,
    webFallback: "Webbläsare",
  },

  palette: {
    placeholder: "Skriv ett kommando…",
    noResults: "Inga matchande kommandon",
    ariaLabel: "Kommandopalett",
    clearAll: "Rensa alla aviseringar",
    glance: "Glance-sammanfattning",
    cancelSnooze: "Avbryt paus",
    copyClip: "Kopiera senast mottagna klipp",
  },

  fileTransfer: {
    sendTitle: "Skicka en fil",
    sendChooseFile: "Välj fil",
    sendDeviceLabel: "Skicka till",
    sendPhoneFallbackName: "Telefon",
    send: "Skicka fil",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Förbereder…" : `Förbereder — ${pct}%`,
    sendDelivering: "Levererar till din telefon…",
    sendWaitingAccept: "Väntar på att din telefon ska acceptera…",
    sendWaitingAppOpen: "Öppna Reflecto på din telefon för att acceptera",
    sendWaitingAppOpenHint:
      "Din telefon kan inte ta emot push-aviseringar och visar den här filen först när Reflecto är öppet.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Laddar upp del ${partNumber} av ${partCount}…`,
    sendDone: "Skickat.",
    sendFailedRejected: "Din telefon avböjde filen.",
    sendFailedNoAnswer: "Inget svar från din telefon.",
    sendFailedUploadFailed: "Uppladdningen misslyckades. Försök igen.",
    sendFailedCancelled: "Avbrutet.",
    sendFailedGeneric: "Något gick fel. Försök igen.",
    sendTooLarge: (maxMiB: number): string => `Filen är för stor (max ${maxMiB} MiB).`,
    sendKeepOpenHint: "Håll den här fliken öppen tills överföringen är klar.",
    sendAnother: "Skicka en till",
    sendNoDevice: "Parkoppla en telefon först för att skicka filer.",
    sendCancel: "Avbryt",
    sendPeerProgress: (chunksDone: number): string =>
      `Telefonen har tagit emot ${chunksDone} block.`,

    receiveTitle: "Inkommande filer",
    receiveOfferFrom: (sender: string): string => `${sender} vill skicka en fil till dig`,
    receiveAccept: "Acceptera",
    receiveDecline: "Avböj",
    receiveReceiving: (n: number, total: number): string => `Tar emot del ${n} av ${total}…`,
    receiveDone: "Sparad.",
    receiveFailed: "Överföringen misslyckades.",
    receiveCancelled: "Avbrutet.",
    receiveCancel: "Avbryt",

    errorChunkAuthFail:
      "Överföringen misslyckades — det gick inte att verifiera datan. Försök igen.",
    errorHashMismatch:
      "Överföringen misslyckades — filen klarade inte integritetskontrollen. Försök igen.",
    errorStorageFull: "Inte tillräckligt med lagringsutrymme för den här överföringen.",
    errorExpired: "Överföringen har gått ut.",
    errorQuotaExceeded: "Den dagliga överföringsgränsen har nåtts — försök igen i morgon.",
    errorInternal: "Överföringen misslyckades — något gick fel.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Stäng",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Överföringsförlopp",
    outgoing: (n: number): string => `Utgående (${n})`,
    incoming: (n: number): string => `Inkommande (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Acceptera alla",
    dropTitle: "Dra och släpp här",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Filer upp till ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Dra och släpp här, eller välj en fil att skicka",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Direkt",
    transportDirectHint: "Skickat direkt mellan enheter — lagras aldrig på våra servrar.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Via server",
    transportRelayHint:
      "Vidarebefordrat via våra servrar, fortfarande totalsäkrat — vi kan inte läsa det.",
    directBadge: "Direkt",
  },
  time: {
    justNow: "nyss",
    minutesAgo: (count: number): string => `${count} min sedan`,
    hoursAgo: (count: number): string => `${count} tim sedan`,
  },
} satisfies MessagesShape;
